// Timing edits reuse the profile's immutable revision/CAS/idempotent storage boundary.
const clone = (v: any) => JSON.parse(JSON.stringify(v));
const blank = () => ({raw: null, normalized: null, source_refs: [], origin: null, confirmed: false, status: 'missing', candidates: []});
export const TIMING_FIELDS: Record<string, any> = {
    coverage: {label: '표에 포함된 범위', options: [['full','원자료 전체'],['partial','일부'],['unknown','범위 불명']]},
    direction: {label: '대운 진행 방향', options: [['forward','순행'],['reverse','역행'],['unknown','기준 불명']]},
    start_rule: {label: '최초 시작 기준 (모르면 unknown)'},
    age_convention: {label: '나이 표시 기준', options: [['international','만 나이'],['year','연 나이'],['counting','세는나이'],['unknown','기준 불명']]},
    year_boundary: {label: '연도 경계 기준', options: [['january1','양력 1월 1일'],['solar_term','절입'],['lunar_new_year','음력 설날'],['source_defined','출처 고유 기준'],['unknown','기준 불명']]},
    period_end: {label: '기간 끝의 포함 여부', options: [['inclusive','끝 포함'],['exclusive','끝 미포함'],['unknown','기준 불명']]},
    date_calendar: {label: '날짜 달력 기준', options: [['solar','양력'],['unknown','기준 불명']]},
    start_age: {label: '시작 나이', number: true}, end_age: {label: '끝 나이', number: true},
    start_date: {label: '시작 날짜 (YYYY-MM-DD)'}, end_date: {label: '끝 날짜 (YYYY-MM-DD)'},
    start_year: {label: '시작 연도', number: true}, end_year: {label: '끝 연도', number: true},
    target_year: {label: '대상 연도', number: true}, target_date: {label: '대상 날짜 (YYYY-MM-DD)'}, target_age: {label: '대상 나이', number: true},
    order: {label: '대운 순서', number: true}, ganzhi: {label: '간지'}, display_period: {label: '원자료의 표시 기간'},
    year: {label: '세운 연도', number: true}, age: {label: '해당 나이', number: true}, as_of_date: {label: '나이 기준 날짜 (YYYY-MM-DD)'}
};
export const BASIS_KEYS = ['direction','start_rule','age_convention','year_boundary','period_end','date_calendar'];
export const TOP_KEYS = ['coverage','start_age','start_date','target_year','target_date','target_age'];
export const DECADE_KEYS = ['order','ganzhi','start_age','end_age','start_year','end_year','start_date','end_date','display_period'];
export const ANNUAL_KEYS = ['year','ganzhi','age','as_of_date'];
export class TimingEditor {
    public active = false;
    public document: any = null;
    public report: any = null;
    public message = '';
    public busy = false;
    public pending: any = null;
    public fields = TIMING_FIELDS;
    public sections: any[] = [];
    private serial = 0;
    constructor(private intake: any) {}
    label(key: string, value: string) { return this.fields[key]?.options?.find((o: any) => o[0] === value)?.[1] || '미제공·기준 불명'; }
    reset() { this.serial++; this.active = false; this.document = null; this.report = null; this.pending = null; this.sections = []; this.message = ''; this.busy = false; }
    start() {
        if (!this.intake.saved || this.intake.dirty || this.intake.locked) return;
        this.document = clone(this.intake.saved.document);
        this.document.schema_version = '1.1.0';
        this.document.timing ||= {contract_version: '1.0.0', selected_set_id: null, sets: []};
        this.active = true; this.report = this.intake.saved.fact_snapshot?.timing || null;
        this.refreshSections();
    }
    cancel() { if (this.busy || this.pending) return; this.reset(); this.intake.dirty = false; }
    refreshSections() {
        this.sections = [];
        for (const group of this.document.timing.sets) {
            this.sections.push({group, title: group.label + ' · 공통 표시', object: group, keys: TOP_KEYS});
            this.sections.push({group, title: group.label + ' · 계산·표시 기준', object: group.basis, keys: BASIS_KEYS});
            for (const kind of ['decades','annual']) group[kind].forEach((row: any, index: number) =>
                this.sections.push({group, title: group.label + ' · ' + (kind === 'decades' ? '대운' : '세운') + ' 행 ' + (index + 1), object: row,
                    keys: kind === 'decades' ? DECADE_KEYS : ANNUAL_KEYS, kind, index}));
        }
    }
    touch() { this.serial++; this.intake.dirty = true; this.report = null; this.message = ''; this.intake.change = null; }
    add() {
        const id = 'timing-' + crypto.randomUUID();
        this.document.timing.sets.push({id, label: '운표 ' + (this.document.timing.sets.length + 1), source_refs: [], basis: {}, decades: [], annual: []});
        this.document.sources.push({id, kind: 'manual', section: 'luck', reference: ''});
        this.touch(); this.refreshSections();
    }
    source(group: any) { return this.document.sources.find((s: any) => s.id === group.id); }
    setSource(group: any, text: string) {
        let source = this.source(group);
        if (!source) { source = {id: group.id, kind: 'manual', section: 'luck', reference: ''}; this.document.sources.push(source); }
        source.reference = text; group.source_refs = [{source_id: group.id, locator: group.label}]; this.touch();
    }
    select(id: string) { this.document.timing.selected_set_id = id || null; this.touch(); }
    addRow(group: any, kind: string) { group[kind].push({}); this.touch(); this.refreshSections(); }
    removeRow(section: any) { section.group[section.kind].splice(section.index, 1); this.touch(); this.refreshSections(); }
    edit(section: any, key: string, part: string, text: string) {
        const f = section.object[key] ||= blank();
        if (part === 'raw') f.raw = text || null;
        else {
            // Preserve the original display when a normalized value is corrected.
            if (f.confirmed && f.raw == null && f.normalized != null) f.raw = String(f.normalized);
            f.normalized = text === '' ? null : this.fields[key].number ? Number(text) : text;
            f.candidates = []; // An explicit edit chooses a value; untouched alternatives remain unresolved.
        }
        f.confirmed = false; f.status = f.normalized == null ? (f.raw ? 'unreadable' : 'missing') : 'candidate';
        this.touch();
    }
    prepared() {
        const d = clone(this.document);
        for (const group of d.timing.sets) {
            const visit = (obj: any) => {
                for (const [key, field] of Object.entries(obj)) {
                    if (!field || typeof field !== 'object' || !('normalized' in field)) continue;
                    const f: any = field;
                    // Existing untouched source fields retain their provenance.
                    if (f.confirmed && f.status === 'user_confirmed' || f.candidates.length) continue;
                    if (f.normalized == null && !f.raw) { obj[key] = blank(); continue; }
                    f.raw ??= String(f.normalized);
                    f.source_refs = [{source_id: group.id, locator: key}]; f.origin = 'direct_input';
                    f.confirmed = f.normalized != null; f.status = f.confirmed ? 'user_confirmed' : 'unreadable'; f.candidates = [];
                }
            };
            visit(group); visit(group.basis); group.decades.forEach(visit); group.annual.forEach(visit);
        }
        return d;
    }
    async validate() {
        const serial = ++this.serial, token = this.intake.token, id = this.intake.profileId, revision = this.intake.baseId;
        const current = () => serial === this.serial && token === this.intake.token && id === this.intake.profileId && revision === this.intake.baseId;
        this.busy = true;
        try { const report = await this.intake.request('/timing-validation', 'POST', this.prepared()); if (current()) { this.report = report; this.message = '입력 자료의 검증을 완료했습니다. 계산 정확성 검증은 아닙니다.'; } }
        catch (e: any) { if (current()) { this.intake.fail(e); this.message = e.message; } }
        finally { if (current()) this.busy = false; }
    }
    async save() {
        if (this.busy || this.intake.locked && !this.pending) return;
        if (!this.pending) this.pending = {path: '/' + this.intake.profileId, method: 'PUT', body: {nickname: this.intake.nickname,
            document: this.prepared(), state: 'confirmed', confirm: true, base_revision_id: this.intake.baseId,
            base_updated_at: this.intake.baseUpdatedAt || undefined, request_id: crypto.randomUUID()}};
        this.intake.pending = this.pending;
        this.busy = true;
        await this.intake.save();
        this.busy = false;
        if (this.intake.status === 'success') {
            const report = this.intake.saved.fact_snapshot.timing;
            this.reset(); this.report = report; this.message = '시기 자료를 새 버전으로 저장했습니다. 오류·부족 자료는 해당 시기 분석에서 제한됩니다.';
        } else if (!this.intake.pending) this.pending = null;
    }
}
